import React, { useState, useEffect } from "react";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Title,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import supabase from "../../../../utils/supabase";
import useUserStore from "../../../../stores/user";
import { FilterState } from "./GradeDistributionFilters";
import { useT } from "../../../../stores/uiLang";
import { TD_T } from "../../../../i18n/teacherDashboard";

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Title,
  Legend
);

interface GradeDistribution {
  labels: string[];
  values: number[];
  total: number;
}

interface GradeDistributionChartProps {
  filters: FilterState;
}

const GradeDistributionChart: React.FC<GradeDistributionChartProps> = ({
  filters,
}) => {
  const t = useT(TD_T);
  const [loading, setLoading] = useState(false);
  const [gradeDistribution, setGradeDistribution] = useState<GradeDistribution>(
    {
      labels: [
        "A (90-100%)",
        "B (80-89%)",
        "C (70-79%)",
        "D (60-69%)",
        "F (Below 60%)",
      ],
      values: [0, 0, 0, 0, 0],
      total: 0,
    }
  );

  const user = useUserStore((state) => state.user);

  // Fetch grade distribution data
  const fetchGradeDistribution = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "getGradeDistribution",
        {
          body: {
            classId: filters.selectedClass || null,
          },
        }
      );

      if (error) {
        console.error("Error fetching grade distribution:", error);
        return;
      }

      if (data && data.distribution) {
        setGradeDistribution(data.distribution);
      }
    } catch (error) {
      console.error("Error fetching grade distribution:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when filters change
  useEffect(() => {
    fetchGradeDistribution();
  }, [filters]);

  const data = {
    labels: gradeDistribution.labels,
    datasets: [
      {
        label: t.numStudents,
        data: gradeDistribution.values,
        backgroundColor: [
          "rgba(16, 185, 129, 0.7)",
          "rgba(59, 130, 246, 0.7)",
          "rgba(249, 115, 22, 0.7)",
          "rgba(245, 158, 11, 0.7)",
          "rgba(239, 68, 68, 0.7)",
        ],
        borderColor: [
          "rgb(16, 185, 129)",
          "rgb(59, 130, 246)",
          "rgb(249, 115, 22)",
          "rgb(245, 158, 11)",
          "rgb(239, 68, 68)",
        ],
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: t.numStudents,
        },
      },
      x: {
        title: {
          display: true,
          text: t.gradeRanges,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const count = context.raw;
            const percent =
              gradeDistribution.total > 0
                ? Math.round((count / gradeDistribution.total) * 100)
                : 0;
            return `${count} students (${percent}%)`;
          },
        },
      },
    },
  };

  const getViewLabel = () => {
    if (filters.selectedClass) {
      return t.selectedClass;
    }
    return t.selectClass;
  };

  return (
    <section className="mb-10">
      <h2 className="text-lg font-medium primary-text mb-5">
        {t.gradeDistribution}
      </h2>
      <div className="bg-white shadow rounded-lg p-6">
        <div className="mb-4">
          <h3 className="text-md font-medium text-gray-900">
            {getViewLabel()}
          </h3>
        </div>
        <div className="relative h-80">
          <Bar data={data} options={options} />
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                <p className="text-sm text-gray-500">
                  {t.loadingDistribution}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default GradeDistributionChart;
